// A tiny synthesized "pop" for toast notifications — no bundled asset, no network.
// Uses a single lazily-created AudioContext (browsers cap the number of contexts,
// so we never create one per toast) and a short sine tone with a smooth gain
// envelope to avoid the click you'd get from starting/stopping a raw oscillator.

type WindowWithWebkitAudio = Window & typeof globalThis & {
  webkitAudioContext?: typeof AudioContext
}

let audioCtx: AudioContext | null = null

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (audioCtx) return audioCtx
  const Ctx = window.AudioContext ?? (window as WindowWithWebkitAudio).webkitAudioContext
  if (!Ctx) return null // Web Audio unsupported — degrade silently
  try {
    audioCtx = new Ctx()
  } catch {
    return null
  }
  return audioCtx
}

/**
 * Play a short, soft notification beep. Safe to call from any browser context:
 * it no-ops when Web Audio is unavailable and swallows any playback error so a
 * failed sound can never break the toast itself. Autoplay policies require a
 * prior user gesture to unlock audio; toasts are almost always raised in
 * response to a click, so the context is already unlocked by then.
 */
export function playNotificationSound(): void {
  const ctx = getAudioContext()
  if (!ctx) return
  try {
    // Some browsers suspend the context until a gesture — resume is a no-op once running.
    if (ctx.state === 'suspended') void ctx.resume()

    const now = ctx.currentTime
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = 'sine'
    // Gentle two-step chirp (E5 -> A5) reads as a friendly "pop" rather than an alarm.
    osc.frequency.setValueAtTime(660, now)
    osc.frequency.exponentialRampToValueAtTime(880, now + 0.09)

    // Quick fade in/out keeps it soft and click-free; peak stays low (0.06) so it
    // never startles.
    gain.gain.setValueAtTime(0.0001, now)
    gain.gain.exponentialRampToValueAtTime(0.06, now + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18)

    osc.connect(gain).connect(ctx.destination)
    osc.start(now)
    osc.stop(now + 0.2)
  } catch {
    /* non-critical — a missing beep must never affect the notification */
  }
}
